import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, FileSignature } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p || 0);

export default function SignContract() {
  const { proposalId } = useParams();
  const [contract, setContract] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/contracts/${proposalId}`);
      if (!res.ok) throw new Error('not found');
      setContract(await res.json());
    } catch (e) {
      setError('Contract not found. Please contact us.');
    }
  }, [proposalId]);

  useEffect(() => { load(); }, [load]);

  const sign = async () => {
    setSigning(true);
    try {
      const res = await fetch(`${API}/api/contracts/${proposalId}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_name: name, agreed }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Signing failed');
      setContract(c => ({ ...c, signed: true, esign: data.esign }));
    } catch (e) {
      setError(e.message);
      setTimeout(() => setError(null), 4000);
    }
    setSigning(false);
  };

  if (!contract && !error) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><Loader2 className="w-10 h-10 text-gold animate-spin" /></div>;
  if (!contract) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><p className="text-white/50">{error}</p></div>;

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-10 px-5">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <FileSignature className="w-6 h-6 text-gold" />
          <h1 className="text-white text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>Exotic Animal Agreement</h1>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-5 mb-5 text-sm">
          <div className="grid grid-cols-2 gap-3 text-white/70">
            <p><span className="text-white/35">Client:</span> {contract.client_name}</p>
            <p><span className="text-white/35">Event:</span> {contract.event_date}{contract.event_time ? ` · ${contract.event_time}` : ''}</p>
            <p className="col-span-2"><span className="text-white/35">Location:</span> {contract.event_location || 'TBD'}</p>
            <p className="col-span-2"><span className="text-white/35">Services:</span> {contract.items.map(i => `${i.name} x${i.quantity}`).join(', ')}</p>
            <p><span className="text-white/35">Total:</span> <span className="text-gold font-semibold">{fmt(contract.total)}</span></p>
          </div>
        </div>

        <div className="bg-white text-black rounded-xl p-6 mb-6 max-h-[45vh] overflow-y-auto whitespace-pre-wrap text-[13px] leading-relaxed" data-testid="agreement-text">
          {contract.agreement_text}
        </div>

        {contract.signed ? (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 text-center" data-testid="contract-signed">
            <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <p className="text-white font-semibold mb-1">Signed by {contract.esign?.signature_name}</p>
            <p className="text-white/40 text-xs">{contract.esign?.signed_at?.slice(0, 19).replace('T', ' ')} UTC — you're all set. See you at the event!</p>
          </div>
        ) : (
          <div className="space-y-4">
            <label className="flex items-start gap-3 text-white/70 text-sm cursor-pointer">
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 accent-[#D4A843]" data-testid="agree-checkbox" />
              I have read and agree to the terms of this Agreement.
            </label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Type your full legal name to sign"
              className="w-full bg-black/50 border border-white/15 rounded-lg px-4 py-3.5 text-white placeholder:text-white/25 focus:outline-none focus:border-gold/60"
              style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic', fontSize: '18px' }}
              data-testid="signature-input"
            />
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <button
              onClick={sign}
              disabled={!agreed || name.trim().length < 3 || signing}
              className="w-full py-4 bg-gold text-black font-bold uppercase tracking-wide disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#E5B954] transition-colors"
              data-testid="sign-button"
            >
              {signing ? 'Signing…' : 'Sign Agreement'}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
